import PhoneInput from "react-phone-number-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  OrderQuantitySelector,
  type CheckoutAppearanceColors,
} from "./OrderQuantitySelector";
import { formatCentsAsUsd } from "@/lib/money";

interface Product {
  _id: string;
  name?: string;
  /** Integer cents */
  price: number;
  payWhatYouCan?: boolean;
}

interface ContactFieldDef {
  key: string;
  label: string;
  required?: boolean;
  options: string[];
}

interface CheckoutEmailStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  phone: string;
  onPhoneChange: (phone: string) => void;
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  /** Cart subtotal in integer cents */
  total: number;
  amountToPay?: string;
  onAmountToPayChange?: (amount: string) => void;
  contactCustomFields?: ContactFieldDef[];
  customFieldValues?: Record<string, string>;
  onCustomFieldValueChange?: (key: string, value: string) => void;
  checkoutColors?: CheckoutAppearanceColors | null;
}

const NATIVE_SELECT_CLASS =
  "border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-input/30 md:text-sm";

export function CheckoutEmailStep({
  email,
  onEmailChange,
  phone,
  onPhoneChange,
  products,
  quantities,
  onQuantityChange,
  total,
  amountToPay = "",
  onAmountToPayChange,
  contactCustomFields,
  customFieldValues = {},
  onCustomFieldValueChange,
  checkoutColors = null,
}: CheckoutEmailStepProps) {
  const hasPayWhatYouCan = products.some((p) => p.payWhatYouCan);
  const displayTotalCents =
    hasPayWhatYouCan && amountToPay
      ? (() => {
          const parsed = parseFloat(amountToPay);
          const cents = Math.round(parsed * 100);
          return !isNaN(parsed) && cents >= total ? cents : total;
        })()
      : total;

  return (
    <div className="space-y-4 flex-1">
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        total={displayTotalCents}
        checkoutColors={checkoutColors}
      />
      <div className="space-y-2">
        <Label htmlFor="customer-email">Email *</Label>
        <Input
          id="customer-email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="customer-phone">Phone *</Label>
        <PhoneInput
          id="customer-phone"
          defaultCountry="US"
          international={false}
          value={phone || undefined}
          onChange={(value) => onPhoneChange(value || "")}
          autoComplete="tel"
          className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
        />
      </div>
      {contactCustomFields != null &&
        contactCustomFields.length > 0 &&
        onCustomFieldValueChange != null &&
        contactCustomFields.map((def) => (
          <div key={def.key} className="space-y-2">
            <Label htmlFor={`custom-field-${def.key}`}>
              {def.label}
              {def.required ? " *" : ""}
            </Label>
            <select
              id={`custom-field-${def.key}`}
              className={NATIVE_SELECT_CLASS}
              value={customFieldValues[def.key] ?? ""}
              onChange={(e) => onCustomFieldValueChange(def.key, e.target.value)}
            >
              <option value="">{def.required ? "Select…" : "— Optional —"}</option>
              {def.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        ))}
      {hasPayWhatYouCan && onAmountToPayChange && (
        <div className="space-y-2">
          <Label htmlFor="amount-to-pay">Amount to pay</Label>
          <div className="flex items-center gap-2">
            <Input
              id="amount-to-pay"
              type="number"
              min={total / 100}
              step="0.01"
              placeholder={formatCentsAsUsd(total)}
              value={amountToPay}
              onChange={(e) => onAmountToPayChange(e.target.value)}
            />
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              (${formatCentsAsUsd(total)} or more)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
